import {
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useState } from 'react';

import { type EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context';

import { usePlacementApi } from '@post-office/use-placement-api';

import type { MessageTemplateContext } from './test-messages/jira-rovo-dev-spotlight-walkthrough';

export type PlacementRenderProps = {
	onDismiss: () => void;
	/** Optional callback passed from Jira to trigger the "Assess risk" action (used by button spotlight) */
	onClick?: () => void;
};

export type PlacementFrontendProps = {
	preloadedInitialData?: {
		messages: Array<EligibleMessage<MessageTemplateContext>>;
	};
};

const placementConfig: PlacementBrowserConfig = {
	placementId: `jira-rovo-dev-spotlight-walkthrough`,
	options: {
		choreographer: {
			isDefaultEnabled: true, // You almost always will want this option to be true. See below for an explanation
		},
		hideInIsolatedCloud: false, // Set to true if your placement should not be shown in IC
	},
};

const placement = {
	createFrontend: createPlacementFrontend(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	PlacementFrontendProps
> = createFrontend<PlacementFrontendProps>((Messages) => ({ preloadedInitialData }) => {
	const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage<MessageTemplateContext>>;
	}>('/', {
		autoRefetch: {
			isEnabled: !preloadedInitialData,
			updateKey: 'jira-rovo-dev-spotlight-walkthrough',
		},
	});

	const messages = preloadedInitialData?.messages || data?.messages;

	const visibleMessages = messages?.filter(
		(message) => !dismissedIds.has(message.messageInstanceId),
	);

	const handleDismiss = (messageInstanceId: string) => {
		setDismissedIds((prev) => new Set([...Array.from(prev), messageInstanceId]));
	};

	if (error) {
		throw error;
	}

	if (!preloadedInitialData && isLoading) {
		return null;
	}

	if (!visibleMessages || visibleMessages.length === 0) {
		return null;
	}

	return (
		<>
			{visibleMessages.map((props) => (
				<Messages.render
					key={props.messageInstanceId}
					onDismiss={() => handleDismiss(props.messageInstanceId)}
					{...props}
				/>
			))}
		</>
	);
});
